import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Download, FileText, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const downloadWithAuth = async (path, fallbackName) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  if (!res.ok) throw new Error('download');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// Section « Mes données » de la page Profil : droits d'accès / portabilité
// (export), rectification des coordonnées du compte, charte signée (preuve
// PDF) et rappel de la procédure d'effacement.
const MesDonnees = ({ user }) => {
  const [coords, setCoords] = useState({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [contact, setContact] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/charte`).then(r => r.json()).then(d => setContact(d.contact_rgpd)).catch(() => {});
  }, []);

  const handleExport = async () => {
    setBusy('export');
    try {
      await downloadWithAuth(`/api/users/${user.id}/donnees`, 'mes_donnees.json');
      toast.success('Vos données ont été téléchargées');
    } catch {
      toast.error("Impossible de télécharger vos données");
    } finally { setBusy(''); }
  };

  const handleProof = async () => {
    setBusy('proof');
    try {
      await downloadWithAuth(`/api/charte/preuves/${user.charte.preuve_id}/pdf`, 'preuve_charte.pdf');
    } catch {
      toast.error('Impossible de télécharger la preuve');
    } finally { setBusy(''); }
  };

  const handleSaveCoords = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: coords.name };
      body.email = coords.email || null;
      body.phone = coords.phone || null;
      const res = await fetch(`${BACKEND_URL}/api/auth/me/coordonnees`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success('Coordonnées mises à jour');
        setTimeout(() => window.location.reload(), 700);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Coordonnées invalides');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally { setSaving(false); }
  };

  const signeLe = user?.charte?.date ? new Date(user.charte.date).toLocaleString('fr-FR') : null;

  return (
    <Card className="glass-effect" data-testid="mes-donnees">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-600" />
          <CardTitle>Mes données personnelles</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSaveCoords} className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Rectifier mes coordonnées</h3>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Votre email ou votre téléphone sert d'identifiant de connexion.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="rgpd-name">Nom</Label>
              <Input id="rgpd-name" value={coords.name} onChange={(e) => setCoords({ ...coords, name: e.target.value })} data-testid="rgpd-name" />
            </div>
            <div>
              <Label htmlFor="rgpd-email">Email</Label>
              <Input id="rgpd-email" type="email" value={coords.email} onChange={(e) => setCoords({ ...coords, email: e.target.value })} data-testid="rgpd-email" />
            </div>
            <div>
              <Label htmlFor="rgpd-phone">Téléphone</Label>
              <Input id="rgpd-phone" value={coords.phone} onChange={(e) => setCoords({ ...coords, phone: e.target.value })} data-testid="rgpd-phone" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="btn-primary" data-testid="rgpd-save-coords">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Enregistrement…' : 'Enregistrer mes coordonnées'}
          </Button>
        </form>

        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Récupérer mes données</h3>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Fichier contenant vos données (compte, profil, événements et comptes rendus rédigés, messages, journaux de connexion…).
          </p>
          <Button variant="outline" onClick={handleExport} disabled={busy === 'export'} data-testid="rgpd-export-btn">
            <Download className="h-4 w-4 mr-2" />{busy === 'export' ? 'Préparation…' : 'Télécharger mes données'}
          </Button>
        </div>

        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Charte d'utilisation</h3>
          {signeLe ? (
            <p className="text-sm text-gray-700 dark:text-slate-300" data-testid="charte-signee-le">
              Signée le {signeLe} (version {user.charte.version}).
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {user?.charte?.preuve_id && (
              <Button variant="outline" onClick={handleProof} disabled={busy === 'proof'} data-testid="rgpd-proof-btn">
                <FileText className="h-4 w-4 mr-2" />Télécharger ma preuve de signature (PDF)
              </Button>
            )}
            <Button variant="outline" asChild>
              <a href="/charte">Relire la charte</a>
            </Button>
          </div>
        </div>

        <div className="space-y-1 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Effacement, limitation, opposition</h3>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Pour demander l'effacement de votre compte ou exercer un autre droit, écrivez à{' '}
            {contact ? <a className="underline" href={`mailto:${contact}`}>{contact}</a> : 'votre responsable'}.
            L'effacement est réalisé par un administrateur : vos données personnelles sont supprimées, et la main
            courante, qui sert de preuve, est conservée sans votre nom.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MesDonnees;
